import { createRootRoute, createRoute, createRouter, Navigate } from "@tanstack/react-router";
import * as React from "react";
import { z } from "zod";
import RootLayout from "@/layouts/RootLayout";
// Real pages
import CompanyInfoPage from "@/pages/configs/CompanyInfoPage";
import ConfigPage from "@/pages/configs/ConfigPage";
import WorkflowPage from "@/pages/configs/WorkflowPage";
import DashboardPage from "@/pages/dashboard/DashboardPage";
import EstimatingCalculatorView from "@/pages/estimating/calculator/EstimatingCalculatorView";
import EstimatingDashboard from "@/pages/estimating/EstimatingDashboard";
import PricingConfigView from "@/pages/estimating/pricing-configs/PricingConfigView";
import MachineLogsPage from "@/pages/factory/machine-logs/MachineLogList";
import MachineLogDetailPage from "@/pages/factory/machine-logs/MachineLogView";
import { FactoryLogList } from "@/pages/factory/production-logs";
import ProductionLogCreate from "@/pages/factory/production-logs/ProductionLogCreate";
import ShopfloorLossTimeAnalyticsPage from "@/pages/factory/ShopfloorLossTimeAnalyticsPage";
import WIPTransferPage from "@/pages/factory/WipTransferList";
import ChangelogPage from "@/pages/help/ChangelogPage";
import ProjectBriefPage from "@/pages/help/ProjectBriefPage";
import UserGuidePage from "@/pages/help/UserGuidePage";
import DeliveryCreate from "@/pages/logistics/DeliveryCreate";
import DeliveryEdit from "@/pages/logistics/DeliveryEdit";
import DeliveryPage from "@/pages/logistics/DeliveryList";
import DeliveryReturnPage from "@/pages/logistics/DeliveryReturn";
import DeliveryDetailPage from "@/pages/logistics/DeliveryView";
import MaterialSupplyBoard from "@/pages/logistics/material-supply/MaterialSupplyBoard";
import { CustomerForm } from "@/pages/master/customers";
import CustomerListPage from "@/pages/master/customers/CustomerList";
import CustomerDetailPage from "@/pages/master/customers/CustomerView";
import MasterDashboard from "@/pages/master/MasterDashboard";
import MaterialTypeList from "@/pages/master/material-types/MaterialTypeList";
import { MaterialForm } from "@/pages/master/materials";
import MaterialListPage from "@/pages/master/materials/MaterialList";
import MaterialDetailPage from "@/pages/master/materials/MaterialView";
import ProductTypeList from "@/pages/master/product-types/ProductTypeList";
import ProductCreate from "@/pages/master/products/ProductCreate";
import ProductEdit from "@/pages/master/products/ProductEdit";
import ProductListPage from "@/pages/master/products/ProductList";
import ProductDetailPage from "@/pages/master/products/ProductView";
import ToolingItemCreatePage from "@/pages/master/tooling/ToolingItemCreatePage";
import ToolingItemEditPage from "@/pages/master/tooling/ToolingItemEditPage";
import ToolingItemListPage from "@/pages/master/tooling/ToolingItemListPage";
import ToolingItemViewPage from "@/pages/master/tooling/ToolingItemViewPage";
import ShopfloorTouchTerminalPage from "@/pages/mes/ShopfloorTouchTerminalPage";
import OutsourceOrderCreate from "@/pages/outsourcing/outsource-orders/OutsourceOrderCreate";
import OutsourceOrderEdit from "@/pages/outsourcing/outsource-orders/OutsourceOrderEdit";
import OutsourceOrdersPage from "@/pages/outsourcing/outsource-orders/OutsourceOrderList";
import OutsourceOrderDetailPage from "@/pages/outsourcing/outsource-orders/OutsourceOrderView";
import OutsourceTrackingHubPage from "@/pages/outsourcing/outsource-orders/OutsourceTrackingHubPage";
import VendorCreate from "@/pages/outsourcing/vendors/VendorCreate";
import VendorEdit from "@/pages/outsourcing/vendors/VendorEdit";
import VendorsPage from "@/pages/outsourcing/vendors/VendorList";
import VendorDetailPage from "@/pages/outsourcing/vendors/VendorView";
import MaterialPlanningPage from "@/pages/planning/material-planning/MaterialPlanningList";
import { MrpDashboard } from "@/pages/planning/mrp/MrpDashboard";
import ProcessPlanningPage from "@/pages/planning/process-planning/ProcessPlanningList";
import { QCReports } from "@/pages/quality/reports/QCReports";
import F0TrackingBoard from "@/pages/planning/production-orders/F0TrackingBoard";
import PaperAllocationAndSurplusHubPage from "@/pages/planning/production-orders/PaperAllocationAndSurplusHubPage";
import PrepressQueuePage from "@/pages/planning/production-orders/PrepressQueuePage";
import PrepressWizard from "@/pages/planning/production-orders/PrepressWizard";
import ProductionMilestoneMatrixPage from "@/pages/planning/production-orders/ProductionMilestoneMatrixPage";
import ProductionOrderCreate from "@/pages/planning/production-orders/ProductionOrderCreate";
import ProductionOrderEdit from "@/pages/planning/production-orders/ProductionOrderEdit";
import ProductionOrdersPage from "@/pages/planning/production-orders/ProductionOrderList";
import ProductionOrderDetailPage from "@/pages/planning/production-orders/ProductionOrderView";
import { CTPTrackingPage } from "@/pages/production/ctp/CTPTrackingPage";
import PrintLayoutView from "@/pages/production/prepress/PrintLayoutView";
import IqcCreatePage from "@/pages/quality/iqc/IqcCreatePage";
import NCRCreate from "@/pages/quality/ncr/NCRCreate";
import NCREdit from "@/pages/quality/ncr/NCREdit";
import NCRPage from "@/pages/quality/ncr/NCRList";
import NCRDetailPage from "@/pages/quality/ncr/NCRView";
import QCCheckEdit from "@/pages/quality/qc-check/QCCheckEdit";
import QCFinalCreate from "@/pages/quality/qc-final/QCFinalCreate";
import QCFinalEdit from "@/pages/quality/qc-final/QCFinalEdit";
import QCFinalPage from "@/pages/quality/qc-final/QCFinalList";
import QCFinalDetailPage from "@/pages/quality/qc-final/QCFinalView";
import QCMaterialCreate from "@/pages/quality/qc-material/QCMaterialCreate";
import QCMaterialEdit from "@/pages/quality/qc-material/QCMaterialEdit";
import QCMaterialsPage from "@/pages/quality/qc-material/QCMaterialList";
import QCMaterialDetailPage from "@/pages/quality/qc-material/QCMaterialView";
import QCProcessCreate from "@/pages/quality/qc-process/QCProcessCreate";
import QCProcessEdit from "@/pages/quality/qc-process/QCProcessEdit";
import QCProcessPage from "@/pages/quality/qc-process/QCProcessList";
import QCProcessDetailPage from "@/pages/quality/qc-process/QCProcessView";
import QCTemplateCreate from "@/pages/quality/qc-templates/QCTemplateCreate";
import QCTemplateEdit from "@/pages/quality/qc-templates/QCTemplateEdit";
import QCTemplatesPage from "@/pages/quality/qc-templates/QCTemplateList";
import QCTemplateDetailPage from "@/pages/quality/qc-templates/QCTemplateView";
import ReportsDashboardPage from "@/pages/reports/ReportsDashboardPage";
import CreateOrderPage from "@/pages/sales/orders/OrderCreate";
import OrderEditPage from "@/pages/sales/orders/OrderEdit";
import OrdersPage from "@/pages/sales/orders/OrderList";
import OrderDetailPage from "@/pages/sales/orders/OrderView";
import CreateQuotationPage from "@/pages/sales/quotations/QuotationCreate";
import QuotationEditPage from "@/pages/sales/quotations/QuotationEdit";
import QuotationsPage from "@/pages/sales/quotations/QuotationList";
import QuotationDetailPage from "@/pages/sales/quotations/QuotationView";
import PurchasingCreate from "@/pages/supply/PurchasingCreate";
import PurchasingEdit from "@/pages/supply/PurchasingEdit";
import PurchasingPage from "@/pages/supply/PurchasingList";
import PurchasingDetailPage from "@/pages/supply/PurchasingView";
import MachinesPage from "@/pages/system/machines/MachineList";
import MachineDetailPage from "@/pages/system/machines/MachineView";
import ProcessLibraryPage from "@/pages/system/processes/ProcessList";
import ProcessDetailPage from "@/pages/system/processes/ProcessView";
import RolesPage from "@/pages/system/roles/RoleList";
import RoleDetailPage from "@/pages/system/roles/RoleView";
import TrashPage from "@/pages/system/trash/TrashList";
import UserListPage from "@/pages/system/users/UserList";
import UserDetailPage from "@/pages/system/users/UserView";
import WasteMatrixPage from "@/pages/system/waste-matrix";
import DesignCreate from "@/pages/tech/designs/DesignCreate";
import DesignEdit from "@/pages/tech/designs/DesignEdit";
import DesignList from "@/pages/tech/designs/DesignList";
import DesignView from "@/pages/tech/designs/DesignView";
import LayoutsPage from "@/pages/tech/layouts/LayoutList";
import LayoutDetailPage from "@/pages/tech/layouts/LayoutView";
import LayoutFormPage from "@/pages/tech/layouts/LayoutFormPage";
import MoldForm from "@/pages/tech/molds/MoldForm";
import MoldsPage from "@/pages/tech/molds/MoldList";
import MoldDetailPage from "@/pages/tech/molds/MoldView";
import CtpPlateLogList from "@/pages/tech/plates/CtpPlateLogList";
import PlateForm from "@/pages/tech/plates/PlateForm";
import PlatesPage from "@/pages/tech/plates/PlateList";
import PlateDetailPage from "@/pages/tech/plates/PlateView";
import TechnicalSpecCreate from "@/pages/tech/technical-specs/TechnicalSpecCreate";
import TechnicalSpecEdit from "@/pages/tech/technical-specs/TechnicalSpecEdit";
import TechnicalSpecList from "@/pages/tech/technical-specs/TechnicalSpecList";
import TechnicalSpecDetailPage from "@/pages/tech/technical-specs/TechnicalSpecView";
import MobileInventoryCheck from "@/pages/warehouse/inventory/MobileInventoryCheck";
import StockCardPage from "@/pages/warehouse/inventory/StockCardPage";
import { WarehouseModule } from "@/pages/warehouse/WarehouseModule";
import { WarehouseTypeModule } from "@/pages/warehouse/WarehouseTypeModule";
import APReconciliationPrintPage from "./pages/finance/ap/APReconciliationPrint";

// --- NEW SILO WAREHOUSE ROUTES ---






























import { APLedgerView, APSummaryListPage } from "@/pages/finance/ap";
import { ARLedgerView, ARReconciliationListPage, ARReconciliationPrintPage, ARSummaryListPage } from "@/pages/finance/ar";
import { ReceiptCreate, ReceiptEdit, ReceiptList, ReceiptsLayout, ReceiptView } from "@/pages/finance/receipts";
import MyRequestsPage from "@/pages/profile/MyRequestsPage";
import UserProfilePage from "@/pages/profile/UserProfilePage";
import VendorDebtList from "@/pages/purchasing/debts/VendorDebtList";
import CustomerDebtList from "@/pages/sales/debts/CustomerDebtList";
import AccountSettingsPage from "@/pages/system/AccountSettingsPage";
// Auth pages removed - handled by AuthGuard in main.tsx

import ErrorComponent from "@/components/shared/ErrorComponent";
import LandingPage from "@/pages/landing/LandingPage";
import AndonLiveBoardPage from "@/pages/mes/AndonLiveBoardPage";
import MaintenanceControlPage from "@/pages/mes/MaintenanceControlPage";
import MesDashboardPage from "@/pages/mes/MesDashboardPage";
import MesSessionPage from "@/pages/mes/MesSessionPage";
import OeeAnalyticsPage from "@/pages/mes/OeeAnalyticsPage";
import FeedbackListPage from "@/pages/system/feedback/FeedbackList";
import FeedbackDetailModal from "@/pages/system/feedback/FeedbackDetailModal";

export const listSearchSchema = z.object({
    page: z.coerce.number().catch(1).optional(),
    pageSize: z.coerce.number().catch(25).optional(),
    search: z.string().optional(),
    q: z.string().optional(),
    sortBy: z.string().optional(),
    sortDesc: z.boolean().optional(),
}).passthrough();

// 1. Create Root Route (Layout)
const rootRoute = createRootRoute({
    component: RootLayout,
    errorComponent: ErrorComponent,
});

// 2. Dashboard (App Grid)
const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/",
    component: DashboardPage,
});

const analyticsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/analytics",
    component: () => <Navigate to="/" replace />,
});

const executiveRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/executive",
    component: () => <Navigate to="/" replace />,
});

const reportsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/reports",
    component: ReportsDashboardPage,
});

const reportSalesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/reports/sales",
    component: ReportsDashboardPage,
});

const reportProductionRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/reports/production",
    component: ReportsDashboardPage,
});

const reportInventoryRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/reports/inventory",
    component: ReportsDashboardPage,
});

const reportARAgingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/reports/ar-aging",
    component: ReportsDashboardPage,
});

const masterDashboardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/master",
    component: MasterDashboard,
});

// Customers (CRUD workflow)
const customersRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/customers",
    component: CustomerListPage,
    validateSearch: listSearchSchema,
});

const customerCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/customers/create",
    component: CustomerForm,
});

const customerDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/customers/$customerId",
    component: CustomerDetailPage,
});

const customerEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/customers/$customerId/edit",
    component: CustomerForm,
});

// Products (CRUD workflow)
const productsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/products",
    component: ProductListPage,
    validateSearch: listSearchSchema,
});

const productCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/products/create",
    component: ProductCreate,
});

const productDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/products/$productId",
    component: ProductDetailPage,
    validateSearch: (search: Record<string, unknown>) => ({
        returnUrl: (search.returnUrl as string) || undefined,
        tab: (search.tab as string) || undefined,
    }),
});

const productEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/products/$productId/edit",
    component: ProductEdit,
    validateSearch: (search: Record<string, unknown>) => ({
        returnUrl: (search.returnUrl as string) || undefined,
    }),
});


const productTypesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/product-types",
    validateSearch: listSearchSchema,
    component: ProductTypeList,
});

// Materials (CRUD workflow)
const materialsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/materials",
    component: MaterialListPage,
    validateSearch: listSearchSchema,
});

const materialCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/materials/create",
    component: MaterialForm,
});

const materialDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/materials/$materialId",
    component: MaterialDetailPage,
});

const materialEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/materials/$materialId/edit",
    component: MaterialForm,
});

const materialTypesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/material-types",
    validateSearch: listSearchSchema,
    component: MaterialTypeList,
});

// Tooling (CRUD workflow)
const toolingMasterRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/master/tooling",
    component: ToolingItemListPage,
    validateSearch: listSearchSchema,
});

const toolingMasterCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/master/tooling/new",
    component: ToolingItemCreatePage,
});

const toolingMasterDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/master/tooling/$id",
    component: ToolingItemViewPage,
});

const toolingMasterEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/master/tooling/$id/edit",
    component: ToolingItemEditPage,
});

// Orders
const ordersRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/orders",
    component: OrdersPage,
    validateSearch: listSearchSchema,
});

const orderDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/orders/$orderId",
    component: OrderDetailPage,
});


const createOrderRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/orders/create",
    component: CreateOrderPage,
    validateSearch: (search: Record<string, unknown>) => search,
});

const orderEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/orders/$orderId/edit",
    component: OrderEditPage,
});

// Estimating
const pricingConfigsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/pricing-configs",
    component: PricingConfigView,
});

const estimatingPricingConfigsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/estimating/pricing-configs",
    component: PricingConfigView,
});

const estimatingCalculatorRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/estimating/calculator",
    component: EstimatingCalculatorView,
});

const estimatingDashboardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/estimating/dashboard",
    component: EstimatingDashboard,
});

// Quotations
const quotationsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/quotations",
    component: QuotationsPage,
    validateSearch: listSearchSchema,
});

const createQuotationRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/quotations/create",
    component: CreateQuotationPage,
});

const quotationDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/quotations/$quotationId",
    component: QuotationDetailPage,
});

const quotationPrintRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/quotations/$quotationId/print",
    component: React.lazy(() => import("./pages/sales/quotations/QuotationPrint")),
});

const quotationEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/quotations/$quotationId/edit",
    component: QuotationEditPage,
});

const debtsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/debts",
    validateSearch: listSearchSchema,
    component: CustomerDebtList,
});

const salesDebtsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/sales/debts",
    validateSearch: listSearchSchema,
    component: CustomerDebtList,
});

// Receipts
const receiptsLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance/receipts",
    component: ReceiptsLayout,
});

const receiptsRoute = createRoute({
    getParentRoute: () => receiptsLayoutRoute,
    path: "/",
    component: ReceiptList,
});

const receiptCreateRoute = createRoute({
    getParentRoute: () => receiptsLayoutRoute,
    path: "/create",
    component: ReceiptCreate,
});

const receiptDetailRoute = createRoute({
    getParentRoute: () => receiptsLayoutRoute,
    path: "/$receiptId",
    component: ReceiptView,
});


const receiptEditRoute = createRoute({
    getParentRoute: () => receiptsLayoutRoute,
    path: "/$receiptId/edit",
    component: ReceiptEdit,
});

const arSummaryRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance/ar",
    validateSearch: listSearchSchema,
    component: ARSummaryListPage,
});

const arLedgerRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance/ar/ledger/$id",
    component: ARLedgerView,
});

const apSummaryRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance/ap",
    validateSearch: listSearchSchema,
    component: APSummaryListPage,
});

const apLedgerRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance/ap/ledger/$id",
    component: APLedgerView,
});

const arReconciliationPrintRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance/ar/reconciliations/$reconciliationId/print",
    component: ARReconciliationPrintPage,
});

const apReconciliationPrintRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance/ap/reconciliations/$reconciliationId/print",
    component: APReconciliationPrintPage,
});

const arReconciliationRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance/ar/reconciliations",
    validateSearch: listSearchSchema,
    component: ARReconciliationListPage,
});

const apReconciliationRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance/ap/reconciliations",
    validateSearch: listSearchSchema,
    component: ARReconciliationListPage,
});

const financeReconciliationRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance/reconciliations",
    validateSearch: listSearchSchema,
    component: ARReconciliationListPage,
});

const financeRootAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance",
    component: ReceiptsLayout,
});

const financeArAgingAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/finance/ar-aging",
    validateSearch: listSearchSchema,
    component: ARSummaryListPage,
});

const vendorDebtsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/purchasing/debts",
    validateSearch: listSearchSchema,
    component: VendorDebtList,
});

// Production Orders
const processPlanningRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/process-planning",
    component: ProcessPlanningPage,
});

const factoryProcessPlanningRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/factory/process-planning",
    component: ProcessPlanningPage,
});

const productionSchedulingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production-scheduling",
    component: ProcessPlanningPage,
});

const mrpRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/planning/mrp",
    component: MrpDashboard,
});

const productionOrdersRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production-orders",
    component: ProductionOrdersPage,
});


const productionOrderDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production-orders/$orderId",
    component: ProductionOrderDetailPage,
    validateSearch: (search: Record<string, unknown>) => search,
});

const productionOrderCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production-orders/create",
    component: ProductionOrderCreate,
    validateSearch: (search: Record<string, unknown>) => search,
});

const prepressWizardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production-orders/prepress-wizard",
    component: PrepressWizard,
});

const productionOrderEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production-orders/$orderId/edit",
    component: ProductionOrderEdit,
});

const trackingDashboardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production-orders/tracking",
    validateSearch: listSearchSchema,
    component: F0TrackingBoard,
});

const pendingPrepressRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production/prepress",
    component: () => <Navigate to="/layouts" search={{ tab: "cho_binh" } as any} replace />,
});

const createPrintLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production/prepress/create",
    component: () => <Navigate to="/layouts/create" replace />,
});

const printLayoutListRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production/print-layouts",
    component: () => <Navigate to="/layouts" search={{ tab: "tat_ca_bai_binh" } as any} replace />,
});

const printLayoutViewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production/print-layouts/$layoutId",
    component: PrintLayoutView,
});

const ctpTrackingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production/ctp-tracking",
    component: CTPTrackingPage,
});

// Material Planning
const materialPlanningRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/material-planning",
    component: MaterialPlanningPage,
});

const materialSupplyBoardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/material-supply-board",
    validateSearch: listSearchSchema,
    component: MaterialSupplyBoard,
});



// Purchasing
const purchasingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/purchasing",
    component: PurchasingPage,
    validateSearch: listSearchSchema,
});

const purchaseOrdersAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/purchase-orders",
    component: PurchasingPage,
    validateSearch: listSearchSchema,
});

const purchasingDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/purchasing/$poId",
    component: PurchasingDetailPage,
});

const purchasingCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/purchasing/create",
    component: PurchasingCreate,
    validateSearch: (search: Record<string, unknown>) => search,
});

const purchasingEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/purchasing/$poId/edit",
    component: PurchasingEdit,
});

// Factory

const productionLogsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/factory/production-logs",
    component: FactoryLogList,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            tab: (search.tab as string) || "all",
        };
    },
});

const productionLogsAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production-logs",
    component: FactoryLogList,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            tab: (search.tab as string) || "all",
        };
    },
});

const productionLogCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/factory/production-logs/create",
    component: ProductionLogCreate,
});

const productionLogCreateAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production-logs/create",
    component: ProductionLogCreate,
});

const factoryPackingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/factory/packing",
    validateSearch: listSearchSchema,
    component: React.lazy(() => import("./pages/factory/packing/FactoryPackingPage")),
});

const machineLogsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/machine-logs",
    validateSearch: listSearchSchema,
    component: MachineLogsPage,
});

const machineLogDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/machine-logs/$logId",
    component: MachineLogDetailPage,
});




const wipTransferRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/wip-transfer",
    component: WIPTransferPage,
});

const wipTransfersAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/wip-transfers",
    component: WIPTransferPage,
});

const machinesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/machines",
    validateSearch: listSearchSchema,
    component: MachinesPage,
});

const machineCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/machines/new",
    component: React.lazy(() => import("./pages/system/machines/MachineFormPage")),
});

const machineDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/machines/$machineId",
    component: MachineDetailPage,
});

const machineEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/machines/$machineId/edit",
    component: React.lazy(() => import("./pages/system/machines/MachineFormPage")),
});

// Tech
const layoutsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/layouts",
    validateSearch: (search: Record<string, unknown>) => ({
        tab: typeof search.tab === "string" ? search.tab : undefined,
    }),
    component: LayoutsPage,
});

const layoutCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/layouts/create",
    validateSearch: (search: Record<string, unknown>) => search,
    component: LayoutFormPage,
});

const layoutDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/layouts/$layoutId",
    component: LayoutDetailPage,
});

const layoutEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/layouts/$layoutId/edit",
    component: LayoutFormPage,
});

const designsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/designs",
    validateSearch: listSearchSchema,
    component: DesignList,
});

const designCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/designs/create",
    component: DesignCreate,
});

const designDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/designs/$designId",
    component: DesignView,
});

const designEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/designs/$designId/edit",
    component: DesignEdit,
});

const moldsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/molds",
    validateSearch: listSearchSchema,
    component: MoldsPage,
});

const toolingTrackingBoardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/tooling-tracking-board",
    validateSearch: listSearchSchema,
    component: () => <Navigate to="/molds" search={{ view: "planning" } as any} replace />,
});

const dieCutDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/molds/$moldId",
    component: MoldDetailPage,
});


const dieCutCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/molds/create",
    component: MoldForm,
});

const dieCutEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/molds/$moldId/edit",
    component: MoldForm,
});



const platesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/plates",
    validateSearch: listSearchSchema,
    component: PlatesPage,
});

const plateCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/plates/create",
    component: PlateForm,
});

const plateDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/plates/$plateId",
    component: PlateDetailPage,
});


const plateEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/plates/$plateId/edit",
    component: PlateForm,
});

const ctpPlateLogsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/plates/ctp-logs",
    validateSearch: listSearchSchema,
    component: CtpPlateLogList,
});

const specsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/specs",
    component: TechnicalSpecList,
    validateSearch: listSearchSchema,
});

const specCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/specs/create",
    component: TechnicalSpecCreate,
});

const specDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/specs/$specId",
    component: TechnicalSpecDetailPage,
});

const specEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/specs/$specId/edit",
    component: TechnicalSpecEdit,
});

const processesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/processes",
    validateSearch: listSearchSchema,
    component: ProcessLibraryPage,
});

const wasteMatrixRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/waste-matrix",
    component: WasteMatrixPage,
});

const auditLogsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/system/audit-logs",
    component: React.lazy(() => import("./pages/system/audit-logs/AuditLogListPage")),
});

const userEventsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/system/user-events",
    component: React.lazy(() => import("./pages/system/user-events/UserEventsPage")),
});

const auditLogsAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/audit-logs",
    component: React.lazy(() => import("./pages/system/audit-logs/AuditLogListPage")),
});

const processDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/processes/$processId",
    component: ProcessDetailPage,
});

// Outsourcing
const vendorsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/vendors",
    validateSearch: listSearchSchema,
    component: VendorsPage,
});

const vendorCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/vendors/create",
    component: VendorCreate,
});

const vendorDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/vendors/$vendorId",
    component: VendorDetailPage,
});

const vendorEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/vendors/$vendorId/edit",
    component: VendorEdit,
});

const outsourceOrdersRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/outsource-orders",
    validateSearch: listSearchSchema,
    component: OutsourceOrdersPage,
});

const outsourceOrderCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/outsource-orders/create",
    component: OutsourceOrderCreate,
});
const outsourceOrderDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/outsource-orders/$orderId",
    component: OutsourceOrderDetailPage,
});

const outsourceOrderEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/outsource-orders/$orderId/edit",
    component: OutsourceOrderEdit,
});

const outsourceTrackingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/outsource-orders/tracking",
    validateSearch: listSearchSchema,
    component: OutsourceTrackingHubPage,
});

const outsourceTrackingHubAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/outsource-orders/tracking-hub",
    validateSearch: listSearchSchema,
    component: OutsourceTrackingHubPage,
});

const milestoneMatrixRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production-orders/matrix",
    validateSearch: listSearchSchema,
    component: ProductionMilestoneMatrixPage,
});

const prepressQueueRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/production-orders/prepress-queue",
    validateSearch: listSearchSchema,
    component: PrepressQueuePage,
});

const shopfloorLossTimeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/shopfloor/loss-time-analytics",
    validateSearch: listSearchSchema,
    component: ShopfloorLossTimeAnalyticsPage,
});

const shopfloorTerminalRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/shopfloor/terminal",
    component: ShopfloorTouchTerminalPage,
});

const paperAllocationRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/planning/paper-allocation",
    validateSearch: listSearchSchema,
    component: PaperAllocationAndSurplusHubPage,
});


// QC
const qcMaterialsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-materials",
    validateSearch: listSearchSchema,
    component: QCMaterialsPage,
});

const qcMaterialsAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/quality/materials",
    validateSearch: listSearchSchema,
    component: QCMaterialsPage,
});

const iqcCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/quality/iqc/create",
    component: IqcCreatePage,
    validateSearch: (search: Record<string, unknown>) => search,
});

const qcMaterialCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-materials/create",
    component: QCMaterialCreate,
    validateSearch: (search: Record<string, unknown>) => search,
});

const qcMaterialDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-materials/$checkId",
    component: QCMaterialDetailPage,
});

const qcMaterialEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-materials/$checkId/edit",
    component: QCMaterialEdit,
});

const qcProcessRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-process",
    validateSearch: listSearchSchema,
    component: QCProcessPage,
});

const qcProcessCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-process/create",
    component: QCProcessCreate,
});

const qcProcessDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-process/$checkId",
    component: QCProcessDetailPage,
});

const qcProcessEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-process/$checkId/edit",
    component: QCProcessEdit,
});

const qcFinalRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-final",
    validateSearch: listSearchSchema,
    component: QCFinalPage,
});

const qcFinalCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-final/create",
    component: QCFinalCreate,
    validateSearch: (search: Record<string, unknown>) => search,
});

const qcFinalDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-final/$checkId",
    component: QCFinalDetailPage,
});

const qcFinalEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-final/$checkId/edit",
    component: QCFinalEdit,
});

const ncrRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/ncr",
    validateSearch: listSearchSchema,
    component: NCRPage,
});

const ncrDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/ncr/$ncrId",
    component: NCRDetailPage,
});


const ncrCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/ncr/create",
    component: NCRCreate,
});

const ncrEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/ncr/$ncrId/edit",
    component: NCREdit,
});

const qcCheckRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-check",
    // C-17: trang kiểm tra CL cũ đã thay bằng IPQC (/qc-process); prod không còn phiếu cũ
    component: () => <Navigate to="/qc-process" replace />,
});

const qcCheckDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-check/$checkId",
    component: () => <Navigate to="/qc-process" replace />,
});

const qcCheckCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-check/create",
    component: () => <Navigate to="/qc-process" replace />,
});

const qcCheckEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-check/$checkId/edit",
    component: () => <Navigate to="/qc-process" replace />,
});


const qcReportsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc/reports",
    component: QCReports,
});

const qualityReportsAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/quality/reports",
    component: QCReports,
});

const qcTemplatesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-templates",
    component: QCTemplatesPage,
    validateSearch: listSearchSchema,
});

const qcTemplateCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-templates/create",
    component: QCTemplateCreate,
});

const qcTemplateDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-templates/$id",
    component: QCTemplateDetailPage,
});

const qcTemplateEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/qc-templates/$id/edit",
    component: QCTemplateEdit,
});

const mesDashboardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/mes",
    component: MesDashboardPage,
});

const mesSessionRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/mes/session/$id",
    component: MesSessionPage,
});

const andonLiveRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/mes/andon",
    component: AndonLiveBoardPage,
});

const oeeAnalyticsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/mes/oee",
    component: OeeAnalyticsPage,
});

const mesMaintenanceRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/mes/maintenance",
    component: MaintenanceControlPage,
});

const mesIncidentsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/mes/incidents",
    component: MaintenanceControlPage,
});














// --- NEW SILO ROUTES DEFINITION ---












const warehouseMasterDataRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/warehouse/master-data",
});

const warehouseMasterDataListRoute = createRoute({
    getParentRoute: () => warehouseMasterDataRoute,
    path: "/",
    component: WarehouseModule,
});

const warehouseMasterDataCreateRoute = createRoute({
    getParentRoute: () => warehouseMasterDataRoute,
    path: "/new",
    component: React.lazy(() => import("./pages/warehouse/master-data/WarehouseForm").then(m => ({ default: m.WarehouseForm }))),
});

const warehouseMasterDataDetailRoute = createRoute({
    getParentRoute: () => warehouseMasterDataRoute,
    path: "/$id",
    component: React.lazy(() => import("./pages/warehouse/master-data/WarehouseView").then(m => ({ default: m.WarehouseView }))),
});

const warehouseMasterDataEditRoute = createRoute({
    getParentRoute: () => warehouseMasterDataRoute,
    path: "/$id/edit",
    component: React.lazy(() => import("./pages/warehouse/master-data/WarehouseForm").then(m => ({ default: m.WarehouseForm }))),
});

const warehouseModuleRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/warehouse",
    component: WarehouseModule,
});

const warehouseTypeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/warehouse/$warehouseType",
    component: WarehouseTypeModule,
});

const warehouseTypeIndexRoute = createRoute({
    getParentRoute: () => warehouseTypeRoute,
    path: "/",
    component: React.lazy(() => import("./pages/warehouse/inventory/InventoryNXTPage")),
});

const stockCardRoute = createRoute({
    getParentRoute: () => warehouseTypeRoute,
    path: "/stock-card",
    validateSearch: listSearchSchema,
    component: StockCardPage,
});

const mobileInventoryCheckRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/warehouse/inventory-check/mobile",
    component: MobileInventoryCheck,
});

const warehouseTransactionsRoute = createRoute({
    getParentRoute: () => warehouseTypeRoute,
    path: "/transactions",
    component: React.lazy(() => import("./pages/warehouse/transactions/TransactionList").then(m => ({ default: m.TransactionList }))),
});

const warehouseTransactionCreateRoute = createRoute({
    getParentRoute: () => warehouseTypeRoute,
    path: "/transactions/new",
    validateSearch: (search: Record<string, unknown>) => search,
    component: React.lazy(() => import("./pages/warehouse/transactions/TransactionForm").then(m => ({ default: m.TransactionForm }))),
});
const warehouseTransactionDetailRoute = createRoute({
    getParentRoute: () => warehouseTypeRoute,
    path: "/transactions/$id",
    component: React.lazy(() => import("./pages/warehouse/transactions/TransactionView").then(m => ({ default: m.TransactionView }))),
});
const warehouseTransactionEditRoute = createRoute({
    getParentRoute: () => warehouseTypeRoute,
    path: "/transactions/$id/edit",
    component: React.lazy(() => import("./pages/warehouse/transactions/TransactionForm").then(m => ({ default: m.TransactionForm }))),
});

const warehouseStocktakeListRoute = createRoute({
    getParentRoute: () => warehouseTypeRoute,
    path: "/stocktakes",
    component: React.lazy(() => import("./pages/warehouse/stocktakes/StocktakeListPage")),
});
const warehouseStocktakeCreateRoute = createRoute({
    getParentRoute: () => warehouseTypeRoute,
    path: "/stocktakes/create",
    component: React.lazy(() => import("./pages/warehouse/stocktakes/StocktakeFormPage")),
});
const warehouseStocktakeDetailRoute = createRoute({
    getParentRoute: () => warehouseTypeRoute,
    path: "/stocktakes/$id",
    component: React.lazy(() => import("./pages/warehouse/stocktakes/StocktakeDetailPage")),
});
const warehouseStocktakeEditRoute = createRoute({
    getParentRoute: () => warehouseTypeRoute,
    path: "/stocktakes/$id/edit",
    component: React.lazy(() => import("./pages/warehouse/stocktakes/StocktakeFormPage")),
});







const deliveryRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/delivery",
    component: DeliveryPage,
});

const deliveriesAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/deliveries",
    component: DeliveryPage,
});

const deliveryDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/delivery/$deliveryId",
    component: DeliveryDetailPage,
});

const deliveryCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/delivery/create",
    component: DeliveryCreate,
    validateSearch: (search: Record<string, unknown>) => search,
});

const deliveriesCreateAliasRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/deliveries/create",
    component: DeliveryCreate,
    validateSearch: (search: Record<string, unknown>) => search,
});

const deliveryEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/delivery/$deliveryId/edit",
    component: DeliveryEdit,
});

const deliveryReturnRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/delivery/$deliveryId/return",
    component: DeliveryReturnPage,
});






// Landing Page route (No auth required)
const landingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/landingpage",
    component: LandingPage,
});

// System
const usersRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/users",
    validateSearch: listSearchSchema,
    component: UserListPage,
});

const userDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/users/$userId",
    component: UserDetailPage,
});

const userCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/users/new",
    component: React.lazy(() => import("./pages/system/users/UserFormPage")),
});

const userEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/users/$userId/edit",
    component: React.lazy(() => import("./pages/system/users/UserFormPage")),
});

const rolesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/roles",
    component: RolesPage,
});

const roleCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/roles/new",
    component: React.lazy(() => import("./pages/system/roles/RoleFormPage")),
});

const trashRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/trash",
    component: TrashPage,
});

const roleDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/roles/$roleId",
    component: RoleDetailPage,
});

const roleEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/roles/$roleId/edit",
    component: React.lazy(() => import("./pages/system/roles/RoleFormPage")),
});

// Config Master Data (separate from System)
const configRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/configs",
    component: ConfigPage,
});

// Workflow Transitions
const workflowRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/workflows",
    component: WorkflowPage,
});

// Company Info
const companyInfoRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/company-info",
    component: CompanyInfoPage,
});

// Profile Routes
const profileRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/profile",
    component: UserProfilePage,
});

const settingsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/settings",
    component: AccountSettingsPage,
});

const settingsAccountRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/settings/account",
    component: AccountSettingsPage,
});

const requestsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/my-requests",
    component: MyRequestsPage,
});



// Help
const guideRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/guide",
    component: UserGuidePage,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            module: (search.module as string) || undefined,
        };
    },
});

const changelogRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/changelog",
    component: ChangelogPage,
});

const briefRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/project-brief",
    component: ProjectBriefPage,
});

// Feedback
const feedbackRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/feedback",
    component: FeedbackListPage,
});

// Chi tiết phiếu: popup trên danh sách (route con, danh sách giữ nguyên trạng thái bên dưới)
const feedbackDetailRoute = createRoute({
    getParentRoute: () => feedbackRoute,
    path: "$feedbackId",
    component: () => {
        const { feedbackId } = feedbackDetailRoute.useParams();
        return React.createElement(FeedbackDetailModal, { feedbackId });
    },
});

// Build Route Tree


const routeTree = rootRoute.addChildren([
    indexRoute,
    productTypesRoute,
    materialTypesRoute,
    analyticsRoute,
    executiveRoute,
    masterDashboardRoute,
    // Master (Customers CRUD)
    customersRoute,
    customerCreateRoute,
    customerDetailRoute,
    customerEditRoute,
    productsRoute,
    productCreateRoute,
    productDetailRoute,
    productEditRoute,
    materialsRoute,
    materialCreateRoute,
    materialDetailRoute,
    materialEditRoute,
    toolingMasterRoute,
    toolingMasterCreateRoute,
    toolingMasterDetailRoute,
    toolingMasterEditRoute,
    // Sales
    ordersRoute,
    orderDetailRoute,
    createOrderRoute,
    orderEditRoute,
    pricingConfigsRoute,
    estimatingPricingConfigsRoute,
    estimatingCalculatorRoute,
    estimatingDashboardRoute,
    quotationsRoute,
    createQuotationRoute,
    quotationDetailRoute,
    quotationPrintRoute,
    quotationEditRoute,
    debtsRoute,
    salesDebtsRoute,
    vendorDebtsRoute,
    receiptsLayoutRoute.addChildren([
        receiptsRoute,
        receiptCreateRoute,
        receiptDetailRoute,
        receiptEditRoute,
    ]),
    arSummaryRoute,
    financeArAgingAliasRoute,
    arLedgerRoute,
    apSummaryRoute,
    apLedgerRoute,
    arReconciliationPrintRoute,
    apReconciliationPrintRoute,
    arReconciliationRoute,
    apReconciliationRoute,
    financeReconciliationRoute,
    financeRootAliasRoute,
    // Planning
    processPlanningRoute,
    factoryProcessPlanningRoute,
    productionSchedulingRoute,
    productionOrdersRoute,
    pendingPrepressRoute,
    createPrintLayoutRoute,
    printLayoutListRoute,
    printLayoutViewRoute,
    ctpTrackingRoute,
    productionOrderCreateRoute,
    prepressWizardRoute,
    productionOrderEditRoute,
    trackingDashboardRoute,
    productionOrderDetailRoute,

    materialPlanningRoute,
    mrpRoute,
    materialSupplyBoardRoute,

    purchasingRoute,
    purchaseOrdersAliasRoute,
    purchasingDetailRoute,
    purchasingCreateRoute,
    purchasingEditRoute,
    // Factory
    productionLogsRoute,
    productionLogsAliasRoute,
    productionLogCreateRoute,
    productionLogCreateAliasRoute,
    factoryPackingRoute,
    machineLogsRoute,
    machineLogDetailRoute,
    wipTransferRoute,
    wipTransfersAliasRoute,
    machinesRoute,
    machineCreateRoute,
    machineDetailRoute,
    machineEditRoute,
    // Tech
    layoutsRoute,
    layoutCreateRoute,
    layoutDetailRoute,
    layoutEditRoute,
    designsRoute,
    designCreateRoute,
    designDetailRoute,
    designEditRoute,
    moldsRoute,
    toolingTrackingBoardRoute,
    dieCutDetailRoute,
    dieCutCreateRoute,
    dieCutEditRoute,
    platesRoute,
    ctpPlateLogsRoute,
    plateCreateRoute,
    plateDetailRoute,
    plateEditRoute,
    specsRoute,
    specCreateRoute,
    specDetailRoute,
    specEditRoute,
    trashRoute,
    processesRoute,
    wasteMatrixRoute,
    auditLogsRoute,
    auditLogsAliasRoute,
    userEventsRoute,
    processDetailRoute,
    // Outsourcing
    vendorsRoute,
    vendorCreateRoute,
    vendorDetailRoute,
    vendorEditRoute,
    outsourceOrdersRoute,
    outsourceOrderCreateRoute,
    outsourceOrderDetailRoute,
    outsourceOrderEditRoute,
    outsourceTrackingRoute,
    outsourceTrackingHubAliasRoute,
    milestoneMatrixRoute,
    prepressQueueRoute,
    shopfloorLossTimeRoute,
    shopfloorTerminalRoute,
    paperAllocationRoute,
    // Warehouse & Logistics
    warehouseModuleRoute,
    warehouseMasterDataRoute.addChildren([
      warehouseMasterDataListRoute,
      warehouseMasterDataCreateRoute,
      warehouseMasterDataDetailRoute,
      warehouseMasterDataEditRoute,
    ]),
    mobileInventoryCheckRoute,
    warehouseTypeRoute.addChildren([
      warehouseTypeIndexRoute,
      warehouseTransactionsRoute,
      warehouseTransactionCreateRoute,
      warehouseTransactionDetailRoute,
      warehouseTransactionEditRoute,
      stockCardRoute,
      warehouseStocktakeListRoute,
      warehouseStocktakeCreateRoute,
      warehouseStocktakeDetailRoute,
      warehouseStocktakeEditRoute,
    ]),







    deliveryRoute,
    deliveriesAliasRoute,
    deliveryDetailRoute,
    deliveryReturnRoute,
    deliveryCreateRoute,
    deliveriesCreateAliasRoute,
    deliveryEditRoute,
    // QC
    qcMaterialsRoute,
    qcMaterialsAliasRoute,
    iqcCreateRoute,
    qcMaterialCreateRoute,
    qcMaterialDetailRoute,
    qcMaterialEditRoute,
    qcProcessRoute,
    qcProcessCreateRoute,
    qcProcessDetailRoute,
    qcProcessEditRoute,
    qcFinalRoute,
    qcFinalCreateRoute,
    qcFinalDetailRoute,
    qcFinalEditRoute,
    qcCheckRoute,
    qcCheckCreateRoute,
    qcCheckDetailRoute,
    qcCheckEditRoute,

    qcReportsRoute,
    qualityReportsAliasRoute,
    qcTemplatesRoute,
    qcTemplateCreateRoute,
    qcTemplateDetailRoute,
    qcTemplateEditRoute,

    ncrRoute,
    ncrCreateRoute,
    ncrDetailRoute,
    ncrEditRoute,

    // New Factory / Supply routes

    // MES Dashboard Route
    mesDashboardRoute,
    mesSessionRoute,
    andonLiveRoute,
    oeeAnalyticsRoute,
    mesMaintenanceRoute,
    mesIncidentsRoute,

    // System (Users & Roles only)
    usersRoute,
    userCreateRoute,
    userDetailRoute,
    userEditRoute,
    rolesRoute,
    roleCreateRoute,
    roleDetailRoute,
    roleEditRoute,
    // Config Master Data
    configRoute,
    workflowRoute,
    companyInfoRoute,
    // Help removed

    // Help
    guideRoute,
    changelogRoute,
    briefRoute,

    profileRoute,
    settingsRoute,
    settingsAccountRoute,
    requestsRoute,
    // Feedback
    feedbackRoute.addChildren([feedbackDetailRoute]),
    landingRoute,
    reportsRoute,
    reportSalesRoute,
    reportProductionRoute,
    reportInventoryRoute,
    reportARAgingRoute,
]);

// Create Router
export const router = createRouter({
    routeTree,
    defaultNotFoundComponent: () => {
        return (
            <div className="flex h-screen flex-col items-center justify-center bg-slate-50 p-4 text-center">
                <h1 className="text-4xl font-bold text-slate-900 mb-3">404</h1>
                <p className="text-slate-500 mb-8">Không tìm thấy trang yêu cầu.</p>
                <button onClick={() => window.history.back()} className="px-4 py-2 bg-indigo-600 text-white rounded-md" type="button">
                    Quay lại
                </button>
            </div>
        );
    }
});

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}
